/**
 * applyDerivedTopology — the WRITE policy on top of deriveTopology.
 *
 * deriveTopology answers "what does the position say"; this module decides
 * what may be WRITTEN, under the owner's 2026-09-02 policy:
 *  - stored values are authoritative: a differing stored value is never
 *    silently overwritten — it becomes a CONFLICT the caller must put to
 *    the user (one dialog per gesture, not per indication);
 *  - blanks fill silently;
 *  - every field the derive writes is stamped with provenance
 *    (source 'derived' + which layout/grid produced it, kind, offGrid) so
 *    'derived' values can be re-derived when a grid improves while
 *    human-typed values are never displaced.
 *
 * CONTRACTS (c6, locked 2026-09-02):
 *  - "grid says X" chips may be built ONLY from kind:'grid' results — this
 *    module never converts a proximity result into a chip-shaped conflict
 *    presentation; conflicts carry the result kind for the UI to phrase.
 *  - Cancel paths must be INERT: callers only invoke this after a COMMITTED
 *    geometry change.
 */

import type { Indication } from '../../stores/projectStore';
import type { SiteLayout, TopologyFields } from '../layout/types';
import { deriveTopology, type DeriveResult } from './deriveTopology';
import { TOPOLOGY_LEVELS } from './suggest';

// ── Provenance (stored at properties.topologyProvenance) ─────

export interface TopologyProvenance {
  /** per-field origin; absent key = origin unknown (pre-provenance data) */
  fields: Partial<Record<string, 'derived' | 'human'>>;
  /** the derive that last wrote here */
  derived?: {
    kind: 'grid' | 'proximity';
    layoutId: string;
    gridSchemaVersion?: number;
    configHash?: number;
    offGrid?: boolean;
    /** grid-quality flags on the winning cell at write time (c6): after a
     *  fill, derived equals stored and no chip appears — this is what makes
     *  "filled from a grid we knew was doubtful" a findable population and
     *  gives the UI something to badge. Values from GridQualityFlag. */
    flags?: string[];
    at: string;
  };
  /** What a "Keep stored" verdict was decided AGAINST (c6, 2026-09-03):
   *  field → the winning derive's source at decision time. A keep is a
   *  judgement about a SPECIFIC grid state; a corrected grid is new
   *  evidence in the same sense a moved marker is, so this is what lets
   *  suppression become conditional later (resurface once when the grid
   *  the value was judged against no longer exists) instead of permanent.
   *  Present only for chip/dialog keeps — a typed edit clears its field's
   *  entry (the typed value supersedes the kept-against verdict). Written
   *  NOW, used later: stamps minted without it are un-expirable forever. */
  keptAgainst?: Record<string, {
    layoutId: string;
    gridSchemaVersion?: number;
    configHash?: number;
    at: string;
  }>;
}

// ── Plan ─────────────────────────────────────────────────────

export interface TopologyConflict {
  field: string;
  stored: string;
  derived: string;
}

export interface TopologyApplyPlan {
  /** safe writes: blank fills (+ provenance) — apply without asking */
  updates: Record<string, unknown> | null;
  /** stored values that differ from the derive — ask, never overwrite */
  conflicts: TopologyConflict[];
  /** the property updates to apply IF the user chooses the derived values */
  conflictUpdates: Record<string, unknown> | null;
  result: DeriveResult;
}

const FIELD_KEYS = TOPOLOGY_LEVELS as readonly string[];

/**
 * Value equality for conflict detection — the two sides pad differently by
 * construction (derived module is String(n), unpadded; stored production
 * values are mixed "02"/"2"; labels carry their own zeros). A strict
 * compare called stored "02" vs derived "2" a conflict — a false chip.
 * All-digit values compare as integers; everything else case-insensitively.
 */
export function topologyValuesEqual(a: string, b: string): boolean {
  const ta = a.trim();
  const tb = b.trim();
  if (/^\d+$/.test(ta) && /^\d+$/.test(tb)) {
    return parseInt(ta, 10) === parseInt(tb, 10);
  }
  return ta.toUpperCase() === tb.toUpperCase();
}

/**
 * Compute what a (re)derive may write for one indication. Pure — no store
 * access, no writes. Callers batch the conflicts of a whole gesture into
 * ONE dialog.
 *
 * `respectHuman` (chip surfaces): fields tagged source:'human' are skipped
 * entirely — no conflict chip re-litigating a defended value, no re-fill of
 * a deliberately blanked field, and Accept cannot displace them. The MOVE
 * dialogs deliberately do NOT pass it: a physically moved marker is new
 * evidence, and the keep-or-update prompt is explicit, not silent.
 */
export function planTopologyUpdate(
  indication: Pick<Indication, 'type' | 'geometry' | 'properties'>,
  layouts: SiteLayout[],
  opts?: { respectHuman?: boolean },
): TopologyApplyPlan | null {
  const result = deriveTopology(layouts, indication.geometry, {
    indicationType: indication.type,
  });
  if (result.kind === 'unknown') return null;
  // OWNER RULINGS 2026-09-02 (Sgula + Hatzav screenshots): topology WRITES
  // come from the GRID only. A marker overlapping no cell on a gridded site
  // gets nothing (null beats proximity junk — row/infrastructure labels),
  // and grid-less sites get nothing either ("no grid, no suggestions, only
  // manual") — the label-proximity engine no longer writes ANY field. The
  // labeled proximity result is still returned for display-only surfaces.
  if (result.kind !== 'grid') {
    return { updates: null, conflicts: [], conflictUpdates: null, result };
  }

  const props = indication.properties as unknown as Record<string, unknown>;
  const prevProv = (props.topologyProvenance ?? {}) as Partial<TopologyProvenance>;
  const fills: Record<string, unknown> = {};
  const conflictWrites: Record<string, unknown> = {};
  const conflicts: TopologyConflict[] = [];
  const touched: string[] = [];

  for (const key of FIELD_KEYS) {
    const derived = (result.fields as Partial<TopologyFields>)[key as keyof TopologyFields];
    if (!derived) continue; // the derive doesn't clear fields it can't define
    if (opts?.respectHuman && prevProv.fields?.[key] === 'human') continue;
    const stored = typeof props[key] === 'string' ? (props[key] as string).trim() : '';
    if (stored === '') {
      fills[key] = derived;
      touched.push(key);
    } else if (!topologyValuesEqual(stored, derived)) {
      conflicts.push({ field: key, stored, derived });
      conflictWrites[key] = derived;
      touched.push(key);
    }
  }

  // customFields: fill missing keys only; differing keys join the conflicts
  if (result.customFields) {
    const existing = (props.customFields ?? {}) as Record<string, string>;
    const merged: Record<string, string> = { ...existing };
    let changedFill = false;
    for (const [k, v] of Object.entries(result.customFields)) {
      const cur = existing[k]?.trim() ?? '';
      if (cur === '') {
        merged[k] = v;
        changedFill = true;
      } else if (!topologyValuesEqual(cur, v)) {
        conflicts.push({ field: k, stored: cur, derived: v });
        conflictWrites.customFields = { ...existing, ...result.customFields };
      }
    }
    if (changedFill) fills.customFields = merged;
  }

  if (Object.keys(fills).length === 0 && conflicts.length === 0) {
    return { updates: null, conflicts: [], conflictUpdates: null, result };
  }

  const provenance = buildProvenance(props, result, touched);
  const updates = Object.keys(fills).length > 0
    ? { ...fills, topologyProvenance: provenance }
    : null;
  const conflictUpdates = conflicts.length > 0
    ? { ...fills, ...conflictWrites, topologyProvenance: provenance }
    : null;

  return { updates, conflicts, conflictUpdates, result };
}

function buildProvenance(
  props: Record<string, unknown>,
  result: DeriveResult,
  touchedFields: string[],
): TopologyProvenance {
  const prev = (props.topologyProvenance ?? {}) as Partial<TopologyProvenance>;
  const fields: TopologyProvenance['fields'] = { ...(prev.fields ?? {}) };
  for (const f of touchedFields) fields[f] = 'derived';
  const derived: TopologyProvenance['derived'] =
    result.kind === 'grid'
      ? {
          kind: 'grid',
          layoutId: result.source.layoutId,
          gridSchemaVersion: result.source.gridSchemaVersion,
          configHash: result.source.configHash,
          at: new Date().toISOString(),
        }
      : result.kind === 'proximity'
        ? {
            kind: 'proximity',
            layoutId: result.layoutId,
            // offGrid MUST persist: bulk-accept has to separate real grid
            // derivations from label guesses on gridded sites (c6 contract)
            ...(result.offGrid ? { offGrid: true } : {}),
            at: new Date().toISOString(),
          }
        : undefined;
  return {
    fields,
    ...(derived ? { derived } : {}),
    // every provenance REBUILDER must carry keptAgainst or Keep verdicts
    // silently lose their expiry reference on the next machine write
    ...(prev.keptAgainst ? { keptAgainst: prev.keptAgainst } : {}),
  };
}

/** A user hand-edited or defended `field` — record it so later re-derives
 *  never treat the value as machine-owned. Returns the properties patch to
 *  merge. `against` (chip Keep / move-dialog Keep only): the grid source
 *  the verdict was decided against — lets suppression expire when that
 *  grid no longer exists. A typed edit (no `against`) clears the field's
 *  entry: the typed value supersedes the kept-against verdict. */
export function humanEditProvenance(
  props: Record<string, unknown>,
  editedFields: string[],
  against?: { layoutId: string; gridSchemaVersion?: number; configHash?: number },
): { topologyProvenance: TopologyProvenance } {
  const prev = (props.topologyProvenance ?? {}) as Partial<TopologyProvenance>;
  const fields: TopologyProvenance['fields'] = { ...(prev.fields ?? {}) };
  const keptAgainst: NonNullable<TopologyProvenance['keptAgainst']> = { ...(prev.keptAgainst ?? {}) };
  const at = new Date().toISOString();
  for (const f of editedFields) {
    fields[f] = 'human';
    if (against) {
      keptAgainst[f] = {
        layoutId: against.layoutId,
        ...(against.gridSchemaVersion !== undefined ? { gridSchemaVersion: against.gridSchemaVersion } : {}),
        ...(against.configHash !== undefined ? { configHash: against.configHash } : {}),
        at,
      };
    } else {
      delete keptAgainst[f];
    }
  }
  return {
    topologyProvenance: {
      fields,
      ...(prev.derived ? { derived: prev.derived } : {}),
      ...(Object.keys(keptAgainst).length > 0 ? { keptAgainst } : {}),
    },
  };
}
